'use strict';
import defaults from 'lodash/defaults';
import isFunction from 'lodash/isFunction';
import {buildListActions} from './actions';
import {buildDeleteFactory} from './defaultDeleteFactory';
import {buildSyncListFactory} from './defaultSyncListFactory';
import {buildSyncModelFactory} from './defaultSyncModelFactory';
import {buildPageFactory} from './defaultPageFactory';
import {ReduxListEpics} from './ReduxListEpics';
import {buildListReducer} from './reducers';
import {BASE_CACHE_OPTIONS, BASE_OPTIONS, isFilterDataDefault, isProcessing, validateOptions} from './utils';


export {ReduxItem} from './item';

/**
 * @param {ReduxObservableListOptions} o
 * @constructor
 */
export function ReduxList(o) {
  /** @type {ReduxObservableListOptions} */
  const options = {};
  defaults(options, o, BASE_OPTIONS);
  options.useFiltering = options.useAutoFilter || options.useFilterOptions || options.useSortOptions;

  if (!options.pageFactory)
    options.pageFactory = buildPageFactory(options);

  if (!options.syncListFactory)
    options.syncListFactory = buildSyncListFactory(options);

  if (!options.deleteFactory)
    options.deleteFactory = buildDeleteFactory(options);

  if (!options.syncModelFactory)
    options.syncModelFactory = buildSyncModelFactory(options);

  if (!options.isFilterDataDefault)
    options.isFilterDataDefault = isFilterDataDefault(options);

  if (options.cacheOptions) {
    const cacheOptions = {};
    defaults(cacheOptions, options.cacheOptions, BASE_CACHE_OPTIONS);
    options.cacheOptions = cacheOptions;
  }

  // Cache that the list is sortable for the listToArray export function.
  // We expect that most lists will need to be sorted even if there are no options to manage state for.
  if (options.useSortOptions || isFunction(options.applySortOptions))
    options._hasSort = true;

  if (options.useSyncUpdates && !isFunction(options.isProcessing))
    options.isProcessing = isProcessing;

  validateOptions(options);

  this.options = options;
  this.actions = buildListActions(options);
  this.reducers = buildListReducer(this.actions, options);
  this.epics = new ReduxListEpics(this.actions, options, this.reducers);
}

/**
 * @typedef {Object} ReduxAction
 * @property {String} type
 * @property {T} payload
 * @property {R} meta
 * @template T, R
 */

/**
 * @typedef {Function} ReduxActionCreator
 * @property {String} TYPE
 * @param {...*} var_args
 * @return ReduxAction<T,R>
 * @template T
 */

/**
 * @typedef {Object} ReduxList
 * @property {ReduxObservableListOptions} options
 * @property {ReduxObservableListActions} actions
 * @property {ReduxObservableListReducers} reducers
 * @property {ReduxListEpics} epics
 */

/**
 * @typedef {Object} ReduxObservableListOptions
 * @property {String} prefix The prefix for the redux action types. Must be unique across the redux store.
 * @property {Number} [pageSize=20} Size of page to request
 * @property {Number} [syncPageSize=500} Size of page to synchronize at a time
 * @property {Object} [initialState={}] Provide additional initial state properties.
 * @property {AjaxFactoryOptions~getToken} getToken
 * @property {Function} getState Retrieve list state from root state.
 * @property {Logger} [logger]
 * @property {Boolean} [crossDomain=false] Whether to use CORs when making ajax requests.
 * @property {ReduxObservableListOptions~isProcessing} [isProcessing]
 * @property {ReduxObservableListOptions~handleProcessingConflict} [handleProcessingConflict]
 * @property {Function} [isAppReady$] Detect server/network is available and app state is ready to attempt to sync offline cached data
 * @property {Function} [isOnline$] Detect server/network is available
 * @property {Function} [isOffline$] Detect server/network is unavailable
 * @property {?ReduxObservableListCacheOptions} [cacheOptions]
 * @property {ReduxObservableListOptions~listToArray} [listToArray] Convert the list in the state to an array
 *
 * // API Paths:
 * @property {String} baseApiUrl This url will be prepended
 *
 * @property {String|ReduxObservableListOptions~listPath} listPath
 * @property {String} [listParams] Insert params to the list request. Do not include outer ampersands
 * @property {AjaxFactory} [pageFactory]
 * @property {ReduxObservableListOptions~parseListResult} [parseListResult]
 * @property {ReduxObservableListOptions~parseList} [parseList]
 *
 * @property {String|ReduxObservableListOptions~listSyncPath} listSyncPath
 * @property {ReduxObservableListOptions~syncNotifier} syncNotifier
 * @property {AjaxFactory} [syncListFactory]
 * @property {ReduxObservableListOptions~parseListSyncResult} [parseListSyncResult]
 * @property {ReduxObservableListOptions~parseSyncList} [parseSyncList]
 * @property {Number} [syncNotifierThrottle=5000]
 * @property {Number} [syncFilterDebounce=5000]
 *
 * @property {ReduxObservableListOptions~deleteItemPath} [deleteItemPath]
 * @property {String} [deleteParams] Insert params to the delete request. Do not include outer ampersands
 * @property {AjaxFactory} [deleteFactory]
 *
 * // Optional features:
 * @property {Boolean} [hasParentId=true] Whether the list depends on a parent ID to retrieve against. e.g. `/authors/some-author-id/books` vs `/books`
 * @property {Boolean} [useFiltering=false]
 * @property {Boolean} [useAutoFilter=false]
 * @property {Boolean} [useFilterOptions=false]
 * @property {Boolean} [useSortOptions=false]
 * @property {Boolean} [_hasSort=false] Internal option to signal listToArray to sort output.
 * @property {Boolean} [useDelete=false]
 *
 * @property {ReduxObservableListOptions~isFilterDataDefault} [isFilterDataDefault]
 * @property {ReduxObservableListOptions~applyFilterData} [applyFilterData]
 *
 * // With Filter Options:
 * @property {Object} [defaultFilterOptions] Required when useFilterOptions is true.
 * @property {ReduxObservableListOptions~serializeFilterOptions} [serializeFilterOptions] required when useFilterOptions is true.
 *
 * // With Sort Options:
 * @property {ReduxListSortOptions} [defaultSortOptions] required when useSortOptions is true
 * @property {ReduxObservableListOptions~serializeSortOptions} [serializeSortOptions] required when useSortOptions is true
 * @property {ReduxObservableListOptions~applySortOptions} [applySortOptions] required when useSortOptions is true
 *
 * // With Sync Options:
 * @property {Boolean} [useSyncUpdates=false]
 * @property {Boolean} [canSyncCreate=false]
 * @property {AjaxFactory} [syncModelFactory]
 * @property {String|ReduxObservableListOptions~listSyncPath} syncModelPath
 * @property {ReduxObservableListOptions~syncModel} [syncModel] Required if useSyncUpdates=true
 * @property {ReduxObservableListOptions~getSyncModelBody} [getSyncModelBody]
 * @property {Array<String>} [syncModelActionsTypes] All action types that may result in a model update to sync
 * @property {Number} [syncModelAttemptThrottle=1000]
 */

/**
 * @typedef {Object} ReduxObservableListCacheOptions
 * @property {localForage} localForage
 * @property {Function} [getKey] Required if hasParentId == true
 * @property {String} [key] Required if hasParentId == false
 * @property {Number} [throttle=1000]
 * @property {Function} [reviver]
 * @property {Function} [replacer]
 */

/**
 * Emit a sequence to indicate to the list that it should attempt to update.
 * The most trivial example would be long polling with Observble.interval.
 * If using WebSockets, you could subscribe to a channel to be notified of updates.
 *
 * @callback ReduxObservableListOptions~syncNotifier
 * @param {String} parentId
 * @param {Store} store
 * @param {Object} dependencies Optional dependencies injected into epics
 * @return {Observable<Void>} The values of the stream are ignored.
 */

/**
 * @callback ReduxObservableListOptions~isVisible
 * @param {Object} item
 * @param {FilterData} filterData
 * @return {Boolean} Return true to show, false to hide
 */

/**
 * @callback ReduxObservableListOptions~deleteItemPath
 * @param {String} itemId
 * @param {Object} [payload]
 * @return {String} deletion url path
 */

/**
 * @callback ReduxObservableListOptions~serializeFilterOptions
 * @param {Object} initialFilterOptions
 * @param {Object} currentFilterOptions
 * @return {?String} JSON.stringify of object delta
 */

/**
 * @callback ReduxObservableListOptions~serializeSortOptions
 * @param {ReduxListSortOptions} initialSortOptions
 * @param {ReduxListSortOptions} currentSortOptions
 * @return {?String} JSON.stringify of object delta
 */

/**
 * Return true if the sort and filtering options both match the initialState values
 *
 * @callback ReduxObservableListOptions~isFilterDataDefault
 * @param {Object} filterOptions
 * @param {ReduxListSortOptions} sortOptions
 * @return {Boolean}
 */

/**
 * @callback ReduxObservableListOptions~syncScheduler
 * @param {String} parentId
 * @param {Store} store
 * @return {Observable<String>} Output a stream of IDs to update
 */

/**
 * Responsible for extracting the list state in redux as a sorted array of objects.
 * @callback ReduxObservableListOptions~listToArray
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @return {Array<Object>}
 */

/**
 * @callback ReduxObservableListOptions~parseListResult
 * @param {AjaxResponse} response
 * @param {ReduxObservableListOptions~parseList} [parseList]
 * @return {ListResult}
 */

/**
 * @callback ReduxObservableListOptions~parseList
 * @param {Array<Object>} list
 * @return {Object<String, Object>}
 */

/**
 * @callback ReduxObservableListOptions~parseSyncList
 * @param {Object<String, Object>} list
 * @return {Object<String, Object>}
 */

/**
 * @typedef {Object} ListResult
 * @property {Boolean} hasMore Whether there are more pages to request
 * @property {Object<String, Object>} list The items, mapped id to item
 * @property {Number} size The number of items in list
 * @property {Number} total The number of items that exist
 */

/**
 * @callback ReduxObservableListOptions~listPath
 * @param {String} parentId
 * @return {String}
 */

/**
 * @callback ReduxObservableListOptions~parseListSyncResult
 * @param {AjaxResponse} ajaxResponse
 * @return {ListSyncResult}
 */

/**
 * @typedef {Object} ListSyncResult
 * @property {Boolean} hasMore Whether there are more pages to request
 * @property {Object<String, Object>} list The items, mapped id to item or null if the object is to be removed
 */

/**
 * @callback ReduxObservableListOptions~listSyncPath
 * @param {String} parentId
 * @return {String}
 */

/**
 * @callback ReduxObservableListOptions~applyFilterData
 * @param {Array<T>} list
 * @param {Object} [filterOptions]
 * @return {Array}
 */

/**
 * @callback ReduxObservableListOptions~applySortOptions
 * @param {Array<T>} list
 * @param {?ReduxListSortOptions} [sortOptions]
 * @return {Array}
 */

/**
 * Track client changes to list data (e.g. optimistic updates).
 * @callback ReduxObservableListOptions~isProcessing
 * @param {Object} item
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @return {Boolean}
 */

/**
 * Track client changes to list data (e.g. optimistic updates).
 * @callback ReduxObservableListOptions~handleProcessingConflict
 * @param {Object} oldItem
 * @param {Object} newItem
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 * @return {?Object}
 */

/**
 * Resolve discrepancies between client and server data.
 * @callback ReduxObservableListOptions~syncModel
 * @param {Object} existingItem
 * @param {UpdateSyncStatusPayload} payload
 * @return {Object}
 */

/**
 * Optionally make adjustments to the model before sending it to the sync request.
 * This would normally be used for only sending a mutable subset of props, for example.
 * @callback ReduxObservableListOptions~getSyncModelBody
 * @param {Object} existingItem
 * @return {Object}
 */



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/index.js